import { useLanguage, t, formatCount } from "../../../i18n";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { toast } from "react-toastify";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import NafathPromptModal from "../../../components/nafath/NafathPromptModal/NafathPromptModal";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import VerificationRequiredState from "../../../components/common/VerificationRequiredState/VerificationRequiredState";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import MyPostCard from "../../../components/listing/MyPostCard/MyPostCard";
import ROUTES from "../../../config/routes";
import { useAuth } from "../../../features/auth/context/AuthContext";
import getMyRoomListings from "../../../features/listing/services/getMyRoomListings";
import deleteRoomListing from "../../../features/listing/services/deleteRoomListing";
import "./MyPosts.css";
import Footer from "../../../components/layout/Footer/Footer";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function mapStatus(status, isActive) {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  if (normalizedStatus === "approved" && isActive) return "Active";
  if (normalizedStatus === "approved" && !isActive) return "Inactive";
  if (normalizedStatus === "pending") return "Pending";
  if (normalizedStatus === "rejected") return "Rejected";
  return toTitleCase(status || "Pending");
}
function formatCreatedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
function getPrimaryImage(listing = {}) {
  if (listing?.primary_image) {
    return listing.primary_image;
  }
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    const primaryImage = listing.images.find(item => item?.is_primary) || listing.images[0];
    return primaryImage?.url || "";
  }
  return "";
}
function mapApiListingToMyPostCard(listing) {
  return {
    id: listing?.id,
    title: listing?.title || "Untitled Listing",
    image: getPrimaryImage(listing),
    status: mapStatus(listing?.status, listing?.is_active),
    createdAt: formatCreatedDate(listing?.created_at),
    district: listing?.neighbourhood?.name || listing?.district || listing?.address || "Location not specified",
    city: listing?.city?.name || "",
    type: toTitleCase(listing?.room_type),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    price: Number(listing?.rent || 0),
    raw: listing
  };
}
export default function MyPosts() {
  useLanguage();
  useDocumentTitle("My Posts");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showNafathModal, setShowNafathModal] = useState(false);
  const [myPosts, setMyPosts] = useState([]);
  const [listingToDelete, setListingToDelete] = useState(null);
  const [isMyPostsLoading, setIsMyPostsLoading] = useState(true);
  const [myPostsError, setMyPostsError] = useState("");
  const [isDeletingListing, setIsDeletingListing] = useState(false);
  const navigate = useNavigate();
  const {
    user: authUser
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  const isProfileComplete = user?.profile_status === "complete";
  const isNafathVerified = user?.nafath_status === "verified";
  const isVerificationLocked = isProfileComplete && !isNafathVerified;
  const fetchMyPosts = useCallback(async () => {
    try {
      setIsMyPostsLoading(true);
      setMyPostsError("");
      const response = await getMyRoomListings();
      const listingsArray = Array.isArray(response?.data) ? response.data : [];
      const mappedListings = listingsArray.map(mapApiListingToMyPostCard);
      setMyPosts(mappedListings);
    } catch (error) {
      setMyPosts([]);
      setMyPostsError(error?.response?.data?.message || error?.message || "Failed to load your listings.");
    } finally {
      setIsMyPostsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!isVerificationLocked) {
      fetchMyPosts();
    } else {
      setIsMyPostsLoading(false);
    }
  }, [fetchMyPosts, isVerificationLocked]);
  const handleCreatePost = () => {
    if (!isNafathVerified) {
      setShowNafathModal(true);
      return;
    }
    navigate(ROUTES.CREATE_LISTING);
  };
  const handleEdit = listing => {
    navigate(`/edit-listing/${listing.id}`);
  };
  const handleDelete = listing => {
    setListingToDelete(listing);
  };
  const confirmDelete = async () => {
    if (!listingToDelete?.id || isDeletingListing) return;
    try {
      setIsDeletingListing(true);
      const response = await deleteRoomListing(listingToDelete.id);
      const isSuccess = response?.status === true;
      if (!isSuccess) {
        throw new Error(response?.message || "Failed to delete listing.");
      }
      setMyPosts(prev => prev.filter(item => item.id !== listingToDelete.id));
      toast.success(t(response?.message || "Listing deleted successfully"));
      setListingToDelete(null);
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || "Failed to delete listing."));
    } finally {
      setIsDeletingListing(false);
    }
  };
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={user} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={user} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


        <NafathPromptModal show={showNafathModal} onHide={() => setShowNafathModal(false)} />


        <main className="bb-my-posts-main">
          <Container fluid>
            <div className="bb-my-posts-hero">
              <div>
                <h1 className="bb-my-posts-hero__title">{t("My Posts")}</h1>
                <p className="bb-my-posts-hero__subtitle">{t("Manage your room listings, track their status, and keep your posts updated for better visibility.")}


                </p>
              </div>

              {!isVerificationLocked ? <Button onClick={handleCreatePost} className="bb-my-posts-hero__btn">

                  <HiOutlinePlus className="me-2" />{t("Post a Room")}

              </Button> : null}
            </div>

            {isVerificationLocked ? <VerificationRequiredState title={t("Verify with Nafath to start posting rooms")} description={t("You have completed your profile, but Nafath verification is still pending. Complete your one-time verification to post rooms, manage listings, and unlock lister contact access.")} buttonLabel="Verify Now" /> : isMyPostsLoading ? <div className="bb-listings-feed__loader bb-listings-feed__loader--initial">
                <Spinner animation="border" size="sm" />
                <span>{t("Loading your posts...")}</span>
              </div> : myPostsError ? <EmptyState title={t("Unable to load your posts")} description={t(myPostsError)} actionLabel={t("Try Again")} onActionClick={fetchMyPosts} /> : <>
                <div className="bb-my-posts__results">
                  <p className="bb-my-posts__results-text">{t("You have ")}
                  {formatCount(myPosts.length, "post")}
                  </p>
                </div>

                {myPosts.length ? <Row className="g-4">
                    {myPosts.map(listing => <Col key={listing.id} xl={4} md={6}>
                        <MyPostCard listing={listing} onEdit={handleEdit} onDelete={handleDelete} />

                      </Col>)}
                  </Row> : <EmptyState title={t("No posts available")} description={t("You have not created any room listings yet. Start by posting your first room.")} actionLabel={t("Post a Room")} onActionClick={handleCreatePost} />}
              </>}
          </Container>
          <Footer />
        </main>

        <Modal show={!!listingToDelete} onHide={() => {
        if (!isDeletingListing) {
          setListingToDelete(null);
        }
      }} centered>

          <Modal.Header closeLabel={t("Close")} closeButton={!isDeletingListing}>
            <Modal.Title>{t("Delete Post")}</Modal.Title>
          </Modal.Header>

          <Modal.Body>{t("Are you sure you want to delete this post?")}

            {listingToDelete ? <strong> {t(listingToDelete.title || listingToDelete.roomTitle)}</strong> : null}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setListingToDelete(null)} disabled={isDeletingListing}>{t("Cancel")}


            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={isDeletingListing}>

              {isDeletingListing ? <>
                  <Spinner size="sm" animation="border" className="me-2" />{t("Deleting...")}

              </> : t("Delete")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>;
}
